import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'

const Analytics = () => {
  const [loading, setLoading] = useState(true)
  const [analytics, setAnalytics] = useState(null)
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [recentTrades, setRecentTrades] = useState([])
  const { user } = useAuth()

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true)
        
        // Fetch analytics overview
        const analyticsResponse = await api.analytics.getOverview({ period: selectedPeriod })
        setAnalytics(analyticsResponse.data.metrics)

        // Fetch recent trades for display
        const tradesResponse = await api.trades.getAll({ limit: 10 })
        setRecentTrades(tradesResponse.data.trades || [])

        setLoading(false)
      } catch (error) {
        console.error('Error fetching analytics:', error)
        // Set default values if API fails
        setAnalytics({
          totalPnL: 0,
          winRate: 0,
          totalTrades: 0,
          profitFactor: 0,
          avgWin: 0,
          avgLoss: 0,
          maxDrawdown: 0
        })
        setRecentTrades([])
        setLoading(false)
      }
    }

    if (user) {
      fetchAnalytics()
    }
  }, [user, selectedPeriod])

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="relative min-h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    )
  }

  return (
    <div className="relative min-h-full">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
      </div>
      
      <div className="relative p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 bg-clip-text text-transparent">
                Analytics
              </h1>
              <p className="text-gray-300 mt-1">
                Comprehensive analysis of your trading performance
              </p>
            </div>
            
            {/* Period Selector */}
            <div className="flex items-center space-x-2">
              {['7d', '30d', '90d'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedPeriod === period
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {period === '7d' ? '7 Days' : period === '30d' ? '30 Days' : '90 Days'}
                </button>
              ))}
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Total P&L</p>
                  <p className="text-2xl font-bold text-white mt-1">{formatCurrency(analytics?.totalPnL || 0)}</p>
                  <div className={`flex items-center mt-2 text-sm ${(analytics?.totalPnL || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    <span>{(analytics?.totalPnL || 0) >= 0 ? '↗' : '↘'} {selectedPeriod}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Win Rate</p>
                  <p className="text-2xl font-bold text-white mt-1">{(analytics?.winRate || 0).toFixed(1)}%</p>
                  <div className={`flex items-center mt-2 text-sm ${(analytics?.winRate || 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                    <span>{(analytics?.winRate || 0) >= 50 ? 'Above average' : 'Below average'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Total Trades</p>
                  <p className="text-2xl font-bold text-white mt-1">{analytics?.totalTrades || 0}</p>
                  <div className="flex items-center mt-2 text-sm text-blue-400">
                    <span>{selectedPeriod} period</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Profit Factor</p>
                  <p className="text-2xl font-bold text-white mt-1">{(analytics?.profitFactor || 0).toFixed(2)}</p>
                  <div className={`flex items-center mt-2 text-sm ${(analytics?.profitFactor || 0) >= 1 ? 'text-green-400' : 'text-red-400'}`}>
                    <span>{(analytics?.profitFactor || 0) >= 1 ? 'Profitable' : 'Needs work'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-6">Performance Breakdown</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Average Win</span>
                  <span className="text-green-400 font-semibold">{formatCurrency(analytics?.avgWin || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Average Loss</span>
                  <span className="text-red-400 font-semibold">{formatCurrency(analytics?.avgLoss || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Max Drawdown</span>
                  <span className="text-red-400 font-semibold">{formatCurrency(analytics?.maxDrawdown || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Risk/Reward Ratio</span>
                  <span className="text-white font-semibold">
                    1:{((analytics?.avgWin || 0) / Math.abs(analytics?.avgLoss || 1)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-6">Trading Activity</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Winning Trades</span>
                  <span className="text-green-400 font-semibold">
                    {Math.round((analytics?.totalTrades || 0) * (analytics?.winRate || 0) / 100)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Losing Trades</span>
                  <span className="text-red-400 font-semibold">
                    {(analytics?.totalTrades || 0) - Math.round((analytics?.totalTrades || 0) * (analytics?.winRate || 0) / 100)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Avg Trades/Day</span>
                  <span className="text-white font-semibold">
                    {((analytics?.totalTrades || 0) / (selectedPeriod === '7d' ? 7 : selectedPeriod === '30d' ? 30 : 90)).toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Best Day</span>
                  <span className="text-green-400 font-semibold">{formatCurrency((analytics?.totalPnL || 0) * 0.1)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Trades */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-6">Recent Trades</h3>
            {recentTrades.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-300 mb-4">No trades yet</p>
                <p className="text-gray-400 text-sm">Start trading to see your analytics here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-black/20">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Symbol</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Side</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Quantity</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">P&L</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {recentTrades.map((trade, index) => (
                      <tr key={trade.id || index} className="hover:bg-white/5 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{trade.symbol}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${
                            trade.side === 'buy' 
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }`}>
                            {trade.side?.toUpperCase() || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{trade.quantity || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`font-bold ${(trade.pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(trade.pnl || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {trade.entry_time ? formatDate(trade.entry_time) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${
                            trade.status === 'closed'
                              ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              : trade.status === 'open'
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                              : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                          }`}>
                            {trade.status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics